/* ══════════════════════════════════════════════════════════════════
   Wo die Tastatur steht — als zwei Maße und ein Merkmal an der Wurzel.

   **Das eine Weg für alles, was unten klebt** (40-mobiles-grundgeruest.md,
   Schritt 2). Die Browser machen es auf zwei Arten:

   1. **Safari auf dem iPhone** (und Chrome ohne `resizes-content`) lässt
      das Layout stehen und legt die Tastatur darüber. Nur der sichtbare
      Ausschnitt wird kleiner — er steht in `window.visualViewport`. Was
      unten klebt — der Fuß eines Blattes mit „Speichern", der haftende Fuß
      eines Formulars —, läge **unter** der Tastatur (28-designkonzept.md,
      „Was über der Tastatur bleibt, sind 350 px").
   2. **Chrome auf Android** macht seit `interactive-widget=resizes-content`
      (index.html) das Layout selbst kleiner. Dort rückt alles von allein
      über die Tastatur — auch die untere Leiste, und die gehört beim
      Tippen nicht über die Tasten.

   Daraus drei Angaben an `<html>`:

   | Angabe | was sie sagt |
   |---|---|
   | `--tastatur` | wie weit der sichtbare Ausschnitt unten über der Kante des Layouts endet (Fall 1) |
   | `--sicht-oben` | wie weit er oben unter der Kante beginnt (Safari rollt beim Fokus mit) |
   | `data-tastatur="ueber"` \| `"schiebt"` | die Tastatur steht — darüber (iPhone) oder das Layout schiebt (Android) |

   Wer unten klebt, rechnet die Maße ein (`stile/telefon.css`); die untere
   Leiste tritt bei `data-tastatur` zur Seite. Ohne Tastatur sind beide
   Maße `0px`, das Merkmal fehlt, und nichts ändert sich.

   **Gemeldet wird je Bild höchstens einmal.** Beim Aufklappen feuert
   `resize` in schneller Folge — jedes Mal Stilwerte zu setzen hieße, dass
   der Browser dazwischen neu rechnet.
   ══════════════════════════════════════════════════════════════════ */

/** Unter so viel ist es keine Tastatur, sondern eine Leiste des Browsers, die ein- und ausfährt. */
const SCHWELLE = 80;
/** Um so viel muss das Layout kürzer geworden sein, damit es die Tastatur ist (Fall 2). */
const SCHWELLE_LAYOUT = 120;

function tippt(): boolean {
  const el = document.activeElement;
  return el instanceof HTMLElement
    && (el.isContentEditable || el.matches("input:not([type=checkbox], [type=radio], [type=range], [type=button], [type=submit]), textarea"));
}

/** Steht der Fokus in etwas, das unten eine Tastatur oder ein Rad aufzieht? */
function feldAktiv(): boolean {
  if (tippt()) return true;
  const el = document.activeElement;
  return el instanceof HTMLElement && el.matches("select");
}

/* ══════════════ Die Tastatur wird mitgenommen, nicht zurückgelassen (20.09.2026) ══════════════

   Luca, über die installierte Anwendung am iPhone: *„Es ist immer dann, wenn man
   das Bottom Sheet **runterzieht**, und nicht X. … Wenn ich ein Bottom Sheet
   runterziehe, muss ich zweimal drücken, um einen anderen Button zu treffen."*

   **Der Unterschied zwischen Zug und Kreuz ist der Fokus, nicht die Geste.** Wer
   das Kreuz oder den Griff antippt, tippt auf einen **Knopf neben** dem Feld —
   und WebKit nimmt dem Feld dabei von sich aus den Fokus, meldet `focusout` und
   fährt die Tastatur ein. Wer **zieht**, tippt nirgendwo hin: Das Feld
   verschwindet mit seinem Blatt aus dem Baum, und dafür meldet WebKit nichts
   (unten bei der Wache steht die Messung dazu). Die Tastatur hat damit niemand
   je gebeten zu gehen.

   **Gemessen am 20.09.2026** — iOS-Simulator, echte Berührungen, Notizblatt mit
   Fokus im Textfeld, die Tastaturlage über `visualViewport` nachgestellt, je
   drei Durchgänge. „Tastatur weg nach" ist die Zeit ab dem Beginn der Geste;
   2 500 ms ist das Ende des Messfensters, also **nie**:

   | Ausweg | `focusout` von WebKit | Tastatur weg nach |
   |---|---|---|
   | **Ziehgeste** | **0/3 — keines** | **2489 / 2488 / 2494 ms** |
   | **Tipp auf den Grund** | **0/3 — keines** | **2488 / 2484 / 2487 ms** |
   | Kreuz | 3/3 | 560 / 515 / 516 ms |
   | Griff | 3/3 | 505 / 551 / 506 ms |

   Der Grund liegt bei beiden gleich: Beim Zug tippt der Finger auf nichts, beim
   Grund schließt `onMouseDown` das Blatt, **bevor** WebKit den Fokus umsetzen
   kann. In beiden Fällen fällt das Feld stumm aus dem Baum.

   Die Wache unten nimmt das Merkmal daraufhin weg, und genau das macht es
   sichtbar: Die untere Leiste wird sofort wieder gezeichnet — **unter der
   Tastatur, die noch steht.** Der erste Tipp trifft die Tastatur, der zweite den
   Knopf. Das ist Lucas „zweimal drücken", und es ist der Rest, den die Wache vom
   19./20.09. nicht erreichen konnte: Sie räumt das Merkmal auf, nicht die
   Tastatur.

   **Nach dieser Zeile** melden alle vier Auswege `focusout` (je 3/3), und die
   Tastatur ist überall nach einer halben Sekunde weg statt nie:

   | Ausweg | `focusout` | Tastatur weg nach |
   |---|---|---|
   | Ziehgeste | 3/3, bei 337–339 ms (Ende der Geste) | 652 / 652 / 654 ms |
   | Tipp auf den Grund | 3/3 | 542 / 538 / 535 ms |
   | Kreuz | 3/3 | 507 / 502 / 505 ms |
   | Griff | 3/3 | 509 / 520 / 518 ms |

   **Also fragt das Blatt nicht mehr, sondern sagt es.** Wer eine Fläche schließt,
   in der ein Feld den Fokus hält, nimmt ihm den Fokus **vorher** — dann meldet
   WebKit `focusout` wie bei jedem Tipp daneben, die Tastatur fährt ein, und die
   Messung oben läuft auf einem Ereignis statt auf einer Wache.

   `innen` grenzt es ein: Ein Blatt nimmt nur den Fokus mit, der **in ihm** liegt.
   Steht er in einer Maske dahinter, geht er das Blatt nichts an.

   **Nur am Finger** — dieselbe Grenze wie bei `fokusZurueck`
   (`bausteine/Blattvonunten.tsx`): *„Mit der Maus und mit `Tab` bleibt es, wie
   es war."* Am Schreibtisch gibt es keine Tastatur, die im Weg stünde, und es
   gäbe etwas zu verlieren: Ein `Esc` in einem Formular mit Entwurf stellt die
   Nachfrage, und wer dort „Weiter bearbeiten" wählt, soll den Cursor noch da
   haben, wo er ihn gelassen hat.
*/
export function tastaturSchliessen(innen?: Element | null): boolean {
  if (typeof document === "undefined") return false;
  if (window.matchMedia?.("(pointer: coarse)").matches !== true) return false;
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  if (innen && !innen.contains(el)) return false;
  if (!feldAktiv()) return false;
  el.blur();
  return true;
}

/* ══════════════ Die Tastatur ist zu — dann ist sie auch zu (19.09.2026) ══════════════

   Luca: *„Man muss ganz oft zweimal auf den Button klicken … prüfen, warum
   der Fokus so ist."*

   **1. Die Leiste kam erst nach der Tastatur zurück.** Das Merkmal
   `data-tastatur` hing allein am sichtbaren Ausschnitt, und den meldet Safari
   erst, wenn die Tastatur ganz eingefahren ist. Wer „Fertig" tippte und gleich
   danach unten auf ein Fach, tippte eine Drittelsekunde lang ins Leere — oder
   auf die Zeile, die dort lag, wo gleich die Leiste stehen würde. **Jetzt
   gilt: ohne Feld keine Tastatur.** Verlässt der Fokus das Feld, ist das
   Merkmal im nächsten Bild weg, und die Leiste steht, während die Tastatur
   noch hinunterfährt. Nebenbei hört damit das Heranzoomen mit zwei Fingern
   auf, sich als Tastatur auszugeben: Es verkleinert den sichtbaren Ausschnitt
   genauso, und bis heute verschwand dabei die Leiste und der Maskenraum
   bekam unten ein Polster so hoch wie der Zoom.

   **2. Die Seite blieb verschoben stehen.** Safari rollt beim Hineintippen
   das **Dokument**, um das Feld über die Tastatur zu holen — auch wenn es
   gar nicht rollen soll (`body { overflow: hidden }`, `schale.css`), und
   ebenso `overflow: hidden`-Kästen wie `.kasten`. Beim Einfahren der
   Tastatur rollt er das nicht in jedem Fall zurück; in der installierten
   Anwendung ist das ein bekanntes Verhalten von WebKit. Dann steht die ganze
   Schale um einige Dutzend Pixel nach oben verschoben, und was am unteren
   Rand klebt, liegt nicht mehr dort, wo der Finger es erwartet. **Diese
   Anwendung rollt ihr Dokument nie** — jeder Versatz dort ist also ein Rest
   der Tastatur und wird zurückgesetzt, sobald kein Feld mehr den Fokus hat.
   Gerollt wird nur, was wirklich steht; ohne Versatz geschieht nichts. */
function versatzZuruecksetzen(): void {
  if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
  for (const el of [document.body, document.getElementById("wurzel"), document.querySelector(".kasten")]) {
    if (el && (el.scrollTop !== 0 || el.scrollLeft !== 0)) {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    }
  }
}

export function tastaturHorchen(): () => void {
  if (typeof window === "undefined") return () => {};
  const sicht = window.visualViewport;
  const wurzel = document.documentElement;
  let wartet = false;
  /* Die größte Höhe, die das Layout bei dieser Breite hatte. Wird es bei
     gleicher Breite deutlich kürzer, während jemand tippt, ist es die
     Tastatur (Fall 2); ändert sich die Breite, ist es ein neues Fenster. */
  let breite = window.innerWidth;
  let hoechste = window.innerHeight;

  const messen = () => {
    wartet = false;
    if (window.innerWidth !== breite) {
      breite = window.innerWidth;
      hoechste = window.innerHeight;
    } else if (window.innerHeight > hoechste) {
      hoechste = window.innerHeight;
    }
    const unten = sicht ? Math.max(0, window.innerHeight - sicht.height - sicht.offsetTop) : 0;
    const oben = sicht ? Math.max(0, sicht.offsetTop) : 0;
    /* Ohne Feld mit Fokus ist es keine Tastatur (oben, „Die Tastatur ist zu"):
       weder beim Einfahren noch beim Zoomen. */
    const feld = feldAktiv();
    const darueber = unten > SCHWELLE && feld;
    const geschrumpft = hoechste - window.innerHeight > SCHWELLE_LAYOUT && tippt();
    wurzel.style.setProperty("--tastatur", `${darueber ? Math.round(unten) : 0}px`);
    wurzel.style.setProperty("--sicht-oben", `${darueber ? Math.round(oben) : 0}px`);
    /* **Zwei Werte statt einem** (20.09.2026): `ueber` heißt, die Tastatur
       liegt über dem Layout (iPhone) — dort steht die untere Leiste ohnehin
       dahinter, und sie **bleibt stehen**. `schiebt` heißt, das Layout ist
       selbst kürzer geworden (Android, `resizes-content`) — nur dort tritt
       die Leiste zur Seite, sonst stünde sie zwischen Feld und Tasten.

       Der Grund für die Trennung ist Lucas „ich muss zweimal drücken": Was
       ausgeblendet ist, kann man nicht treffen — und ob die Tastatur wirklich
       noch steht, weiß der Browser auf dem iPhone nicht zuverlässig zu sagen
       (er meldet den Fokusverlust nicht, wenn ein Blatt weggezogen wird).
       Eine Leiste, die nie verschwindet, kann auch nicht zu spät
       wiederkommen. */
    if (darueber || geschrumpft) {
      wurzel.dataset["tastatur"] = darueber ? "ueber" : "schiebt";
      wachen();
    } else delete wurzel.dataset["tastatur"];
    if (!feld && unten <= SCHWELLE) versatzZuruecksetzen();
  };
  const anstossen = () => {
    if (wartet) return;
    wartet = true;
    requestAnimationFrame(messen);
  };

  /* ══════════════ Ein Feld, das mit seinem Blatt verschwindet, meldet sich
     nicht ab (20.09.2026) ══════════════

     Luca: *„Insbesondere kommt das Problem mit dem Fokus auf die Bottom-Navbar,
     dass es nicht funktioniert, wenn ich aus einem Bottom-Sheet komme."*

     **WebKit feuert weder `blur` noch `focusout`, wenn das fokussierte Element
     aus dem Dokument fällt.** Am 20.09.2026 im iOS-Simulator (iOS 26.5, Safari
     26.6) nachgemessen — ein Feld bekommt den Fokus, sein Elternknoten wird
     entfernt, mitgeschrieben wird alles: `["focusin:probe", "aktiv-vor:probe",
     "aktiv-nach:BODY"]`. Kein `focusout`, kein `blur`; `document.activeElement`
     wird still zum `body`.

     Genau das passiert, wenn ein Blatt von unten **weggezogen** wird, in dem ein
     Feld den Fokus hatte — die Notiz mit ihrem Textfeld, die Kundensuche, das
     Nachschlagen. Alle Horcher oben schweigen dann: Der sichtbare Ausschnitt
     ändert sich erst, wenn die Tastatur zu Ende eingefahren ist, und ein
     Fokuswechsel kommt nie. **Bis dahin steht `data-tastatur`, und
     damit steht die untere Leiste auf `display: none`** (`stile/telefon.css`)
     und der Maskenraum trägt unten ein Polster so hoch wie die Tastatur. Der
     nächste Tipp landet im Nichts — und der zweite wirkt, weil inzwischen der
     Ausschnitt gemeldet wurde. Das ist Lucas „doppelt drücken".

     **Gemessen** (iOS-Simulator, echte Berührungen, Notizblatt mit Fokus im
     Textfeld, danach ein Tipp auf die Leiste):

     | Blatt geschlossen über | vorher | nachher |
     |---|---|---|
     | Ziehgeste → „Suche" | 0/3 | 3/3 |
     | Grund daneben → „Suche" | 0/3 | 3/3 |
     | Ziehgeste → „Mehr" | 0/3 | 3/3 |
     | Ziehgeste → Plus | 0/3 | 3/3 |
     | Griff → „Suche" | 3/3 | 3/3 |

     (Der Griff war schon vorher in Ordnung: Ein Tipp darauf wechselt den Fokus,
     und der meldet sich.)

     **Also wird nachgesehen, statt auf eine Meldung zu warten.** Solange das
     Merkmal steht, prüft die Wache je Bild, ob überhaupt noch ein Feld den
     Fokus hat. Steht keines mehr, wird gemessen — das nimmt das Merkmal weg,
     setzt `--tastatur` auf null und rollt einen Rest der Tastatur zurück. Die
     Wache läuft **nur**, solange die Tastatur als offen gilt, und hört von
     selbst auf; sie kostet je Bild einen Blick auf `document.activeElement`. */
  let wache = 0;
  function wachen(): void {
    if (wache) return;
    wache = requestAnimationFrame(() => {
      wache = 0;
      if (!wurzel.dataset["tastatur"]) return;
      if (!feldAktiv()) { messen(); return; }
      wachen();
    });
  }

  /* ── Das Feld, in dem man tippt, bleibt über dem haftenden Fuß ──
     Der Browser rollt ein Feld beim Fokus gerade so weit, dass es an der
     Unterkante der Rollfläche steht — und dort haftet der Fuß mit
     „Anlegen". **Gemessen auf 390 × 500:** das Feld „Firma" bei 400–444,
     der Knopf „Kunde anlegen" bei 389–433, der Cursor unter dem Knopf.

     `scroll-margin` am Feld wäre der bequeme Weg und ist der gefährliche:
     Verlangt es mehr Rand, als die Rollfläche hergibt, nimmt das Feld in
     Chrome keine Eingabe mehr an (28-designkonzept.md, „Was über der
     Tastatur bleibt"). Also einmal nachrollen, und nur um die Überdeckung. */
  const feldPruefen = () => {
    if (wurzel.dataset["geraet"] !== "telefon") return;
    const feld = document.activeElement;
    if (!(feld instanceof HTMLElement) || !feld.matches("input, textarea, select")) return;
    const fuss = feld.closest(".fdialog")?.querySelector<HTMLElement>(":scope > footer");
    if (!fuss || fuss.contains(feld) || getComputedStyle(fuss).position !== "sticky") return;
    const f = feld.getBoundingClientRect();
    const u = fuss.getBoundingClientRect();
    if (f.bottom <= u.top - 12 || f.top >= u.bottom) return;
    for (let el = feld.parentElement; el; el = el.parentElement) {
      const stil = getComputedStyle(el);
      if (/(auto|scroll)/.test(stil.overflowY) && el.scrollHeight > el.clientHeight) {
        el.scrollBy({ top: f.bottom + 12 - u.top });
        return;
      }
    }
  };
  // Zweimal: gleich, und wenn die Tastatur ausgefahren ist.
  const beimFokus = () => { requestAnimationFrame(feldPruefen); window.setTimeout(feldPruefen, 400); };
  // Das Merkmal hängt auch am Fokus: Wer das Feld verlässt, hat keine Tastatur mehr.
  const fokusWechsel = () => { anstossen(); };

  messen();
  sicht?.addEventListener("resize", anstossen);
  sicht?.addEventListener("scroll", anstossen);
  sicht?.addEventListener("resize", beimFokus);
  window.addEventListener("resize", anstossen);
  window.addEventListener("resize", beimFokus);
  document.addEventListener("focusin", beimFokus);
  document.addEventListener("focusin", fokusWechsel);
  document.addEventListener("focusout", fokusWechsel);
  return () => {
    sicht?.removeEventListener("resize", anstossen);
    sicht?.removeEventListener("scroll", anstossen);
    sicht?.removeEventListener("resize", beimFokus);
    window.removeEventListener("resize", anstossen);
    window.removeEventListener("resize", beimFokus);
    document.removeEventListener("focusin", beimFokus);
    document.removeEventListener("focusin", fokusWechsel);
    document.removeEventListener("focusout", fokusWechsel);
    if (wache) cancelAnimationFrame(wache);
  };
}
